// ObstacleDragControls.js
import * as THREE from 'three';
import { DragControls } from 'three/addons/controls/DragControls.js';
import { POLYGONS } from '../Global/variables';
import { sceneManager } from "../SetUps/SceneManager";
import { createSegmentPlane } from "../Global/functions";
import { updateObstacleShape } from "./Modifications";
import { updateLinkedSegmentsAfterMove } from "./ObstacleManager";
import { pushUndoAction } from "../Global/UndoManager";
import { getVertices, polygonsOverlapOnPlane } from "../Panels/Helpers";
import { refreshPanelOverlapHighlights } from "../Panels/ValidationVisuals";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";

let obstacleDragControls = null;
let rotationDragControls = null;
let edgeDragControls = null;

// null | 'vertex' | 'rotate' | 'translate'
let currentDragMode = null;

let obstacleHandles = [];
let rotationHandles = [];
let edgeHandles = [];
let active = null;

const HANDLE_COLORS = {
    translate: 0xff0000,
    translateHover: 0xff6666,
    rotate: 0xff0000,
    rotateHover: 0xff6666,
    vertex: 0xff0000, // match the move/edge square handle color
};

const EDGE_HANDLE_STYLE = {
    lineOpacity: 0.35,
    lineHoverOpacity: 0.65,
    blockWidth: 0.12,
    blockDepth: 0.03,
    blockLengthRatio: 0.35,
    blockMin: 0.18,
    blockMax: 0.6,
    blockOpacity: 0.7,
};

const ROTATION_HANDLE_STYLE = {
    innerRadius: 0.12,
    outerRadius: 0.15,
    arc: Math.PI * 0.78,
    arrowRadius: 0,
    arrowSize: 0.075,
};

const ROTATION_HANDLE_NORMAL_LIFT = 0.01;
const ROTATION_HANDLE_CORNER_OFFSET = 0.08;

// For detecting clicks outside the handles
const raycaster = new THREE.Raycaster();
let pointerDownListener = null;

// For hover cursor changes
let hoverMoveListener = null;
let currentCursor = 'auto';

// Rotation drag state
let rotationState = null;

// Translation drag state
let translateState = null;

// Vertex drag state
let vertexDragState = null;

// ---------------------------------------------------------------------
// Segment polygon utilities (used to validate obstacle stays attached)
// ---------------------------------------------------------------------

function getSegmentPolygon2D(segmentIndex) {
    const segment = POLYGONS[segmentIndex];
    if (!segment || !segment.edges || !segment.edges.length) return null;

    const segPoints2D = [];
    segment.edges.forEach(edge => {
        if (edge.point1) segPoints2D.push(new THREE.Vector2(edge.point1.x, edge.point1.y));
        if (edge.point2) segPoints2D.push(new THREE.Vector2(edge.point2.x, edge.point2.y));
    });

    const uniquePoints = [];
    for (let p of segPoints2D) {
        if (!uniquePoints.some(u => u.distanceTo(p) < 1e-6)) uniquePoints.push(p);
    }
    if (!uniquePoints.length) return null;

    const center = uniquePoints
        .reduce((acc, p) => acc.add(p), new THREE.Vector2())
        .divideScalar(uniquePoints.length);

    uniquePoints.sort(
        (a, b) =>
            Math.atan2(a.y - center.y, a.x - center.x) -
            Math.atan2(b.y - center.y, b.x - center.x)
    );

    return uniquePoints;
}

function pointInSegmentPolygon2D(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;

        const intersect =
            ((yi > point.y) !== (yj > point.y)) &&
            (point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi);

        if (intersect) inside = !inside;
    }
    return inside;
}

function getObstacleBoundaryPoints(obstruction) {
    if (!obstruction) return [];
    if (obstruction.mesh?.updateMatrixWorld) obstruction.mesh.updateMatrixWorld(true);
    const storedOffsetOutline = obstruction?.mesh?.userData?.obstacleOffsetOutlineWorld;
    if (Array.isArray(storedOffsetOutline) && storedOffsetOutline.length >= 3) {
        return storedOffsetOutline.map((p) => p.clone());
    }
    const offset = obstruction.mesh?.getObjectByName?.("offset");
    if (offset?.geometry?.attributes?.position) {
        if (offset.updateMatrixWorld) offset.updateMatrixWorld(true);
        return getVertices(offset);
    }
    if (Array.isArray(obstruction.cords)) return obstruction.cords;
    return [];
}

function obstacleOverlapsPanels(obstruction, segmentIndex) {
    const segment = POLYGONS?.[segmentIndex];
    if (!segment || !Array.isArray(segment.panels) || segment.panels.length === 0) return false;

    const plane = createSegmentPlane(segmentIndex);
    if (!plane) return false;
    const normal = plane.normal.clone().normalize();

    const footprints = [];
    const offsetBoundary = getObstacleBoundaryPoints(obstruction);
    if (Array.isArray(offsetBoundary) && offsetBoundary.length >= 3) {
        footprints.push(offsetBoundary);
    }
    const baseBoundary = Array.isArray(obstruction?.cords) ? obstruction.cords : [];
    if (baseBoundary.length >= 3) {
        footprints.push(baseBoundary);
    }
    if (!footprints.length) return false;

    for (const panelData of segment.panels) {
        const panel = panelData?.panel;
        if (!panel) continue;
        if (panel.updateMatrixWorld) panel.updateMatrixWorld(true);
        const panelPoints = getVertices(panel);
        if (!panelPoints.length) continue;
        if (footprints.some((fp) =>
            polygonsOverlapOnPlane(panelPoints, fp, normal, panel.position)
        )) {
            return true;
        }
    }

    return false;
}

function obstacleOverlapsPanelsInLinkedSegments(obstruction, fallbackSegmentIndex = null) {
    if (!obstruction) return false;
    const linked = Array.isArray(obstruction.linkedSegments)
        ? obstruction.linkedSegments
        : [];
    const parent = Number.isInteger(obstruction.parentSegment)
        ? obstruction.parentSegment
        : fallbackSegmentIndex;
    const targets = Array.from(
        new Set([
            ...linked,
            ...(Number.isInteger(parent) ? [parent] : []),
            ...(Number.isInteger(fallbackSegmentIndex) ? [fallbackSegmentIndex] : []),
        ])
    ).filter((idx) => Number.isInteger(idx) && idx >= 0 && idx < POLYGONS.length);

    return targets.some((segmentIndex) => obstacleOverlapsPanels(obstruction, segmentIndex));
}

function syncHandlesToObstacle(obstruction, plane) {
    if (!obstruction || !Array.isArray(obstruction.cords)) return;

    const basePointsUpdated = obstruction.cords.slice(0, obstruction.cords.length - 1);
    if (!basePointsUpdated.length) return;

    const center = new THREE.Vector3();
    basePointsUpdated.forEach(p => center.add(p));
    center.multiplyScalar(1 / basePointsUpdated.length);

    const planeNormal = getPlaneNormal(
        plane || (typeof obstruction.parentSegment === "number" ? createSegmentPlane(obstruction.parentSegment) : null)
    );

    if (obstruction.type === "circle") {
        let radiusDir = basePointsUpdated[0].clone().sub(center);
        if (radiusDir.lengthSq() < 1e-6 && basePointsUpdated.length > 1) {
            radiusDir = basePointsUpdated[Math.floor(basePointsUpdated.length / 4)]
                .clone()
                .sub(center);
        }
        if (radiusDir.lengthSq() < 1e-6) return;

        const radius = Math.sqrt(radiusDir.lengthSq());
        const dirNorm = radiusDir.clone().normalize();

        const scaleHandlePos = center.clone().add(dirNorm.clone().multiplyScalar(radius));
        const translateHandlePos = center.clone().sub(dirNorm.clone().multiplyScalar(radius));

        if (obstacleHandles[0]) {
            obstacleHandles[0].position.copy(scaleHandlePos);
        }
        if (edgeHandles[0]) {
            edgeHandles[0].position.copy(translateHandlePos);
            edgeHandles[0].userData.centerOffset = translateHandlePos.clone().sub(center);
            updateEdgeHandleVisual(edgeHandles[0], null, null, planeNormal);
            if (edgeHandles[0].userData.edgeBlock) {
                edgeHandles[0].userData.edgeBlock.scale.set(
                    EDGE_HANDLE_STYLE.blockMin,
                    EDGE_HANDLE_STYLE.blockWidth,
                    EDGE_HANDLE_STYLE.blockDepth
                );
            }
        }
        return;
    }

    for (let i = 0; i < obstacleHandles.length; i++) {
        obstacleHandles[i].position.copy(basePointsUpdated[Math.min(i, basePointsUpdated.length - 1)]);
    }

    for (let i = 0; i < rotationHandles.length; i++) {
        const corner = basePointsUpdated[i % basePointsUpdated.length].clone();
        updateRotationHandleVisual(rotationHandles[i], center, corner, planeNormal);
    }

    for (let i = 0; i < edgeHandles.length; i++) {
        const p1 = basePointsUpdated[i % basePointsUpdated.length];
        const p2 = basePointsUpdated[(i + 1) % basePointsUpdated.length];
        const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);

        edgeHandles[i].position.copy(mid);
        edgeHandles[i].userData.centerOffset = mid.clone().sub(center);
        updateEdgeHandleVisual(edgeHandles[i], p1, p2, planeNormal);
    }
}

function isWorldPointInsideSegment(point, segmentIndex) {
    const poly = getSegmentPolygon2D(segmentIndex);
    if (!poly) return false;

    const p2d = new THREE.Vector2(point.x, point.y);
    return pointInSegmentPolygon2D(p2d, poly);
}

// ---------------------------------------------------------------------
// Utility: Enable/Disable raycast on the obstacle mesh itself
// ---------------------------------------------------------------------
function setObstacleRaycastEnabled(obstruction, enabled) {
    if (!obstruction?.mesh) return;

    if (enabled) {
        if (obstruction._originalRaycast) {
            obstruction.mesh.raycast = obstruction._originalRaycast;
            obstruction._originalRaycast = null;
        }
    } else {
        if (!obstruction._originalRaycast) {
            obstruction._originalRaycast = obstruction.mesh.raycast;
            obstruction.mesh.raycast = function () { /* no-op */ };
        }
    }
}

function setObstacleOffsetVisuals(obstruction, visible) {
    if (!obstruction?.mesh) return;
    const offset = obstruction.mesh.getObjectByName("offset");
    const connectors = obstruction.mesh.getObjectByName("offsetConnectors");
    if (offset) offset.visible = visible;
    if (connectors) connectors.visible = visible;
}

// ---------------------------------------------------------------------
// Utility: Changing the DOM cursor
// ---------------------------------------------------------------------
function setDomCursor(domElement, cursor) {
    if (!domElement || currentCursor === cursor) return;
    domElement.style.cursor = cursor;
    currentCursor = cursor;
}

function getPlaneNormal(plane) {
    if (plane?.isVector3 && plane.lengthSq() > 1e-6) {
        return plane.clone().normalize();
    }
    if (plane?.normal && plane.normal.lengthSq() > 1e-6) {
        return plane.normal.clone().normalize();
    }
    return new THREE.Vector3(0, 0, 1);
}

function getPlaneBasis(normal) {
    const n = normal.clone().normalize();
    let u = new THREE.Vector3(1, 0, 0);
    if (Math.abs(n.dot(u)) > 0.9) {
        u.set(0, 1, 0);
    }
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    const uOrtho = new THREE.Vector3().crossVectors(v, n).normalize();
    return { u: uOrtho, v, n };
}

function orientObjectToEdge(object, edgeDir, normal) {
    const x = edgeDir.clone().normalize();
    const z = normal.clone().normalize();
    let y = new THREE.Vector3().crossVectors(z, x);
    if (y.lengthSq() < 1e-6) {
        y = new THREE.Vector3(0, 1, 0);
    }
    y.normalize();
    const xOrtho = new THREE.Vector3().crossVectors(y, z).normalize();
    const matrix = new THREE.Matrix4().makeBasis(xOrtho, y, z);
    object.quaternion.setFromRotationMatrix(matrix);
}

function updateEdgeHandleVisual(handle, p1, p2, planeNormal) {
    if (!handle) return;

    if (!p1 || !p2) {
        if (handle.userData.edgeLine) handle.userData.edgeLine.visible = false;
        const normal = getPlaneNormal(planeNormal);
        const quat = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, 1),
            normal.clone().normalize()
        );
        handle.quaternion.copy(quat);
        return;
    }

    const edgeDir = p2.clone().sub(p1);
    const edgeLen = edgeDir.length();
    if (edgeLen < 1e-6) return;

    const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
    handle.position.copy(mid);

    const normal = getPlaneNormal(planeNormal);
    orientObjectToEdge(handle, edgeDir, normal);

    const block = handle.userData.edgeBlock;
    const line = handle.userData.edgeLine;
    if (block) {
        const blockLength = THREE.MathUtils.clamp(
            edgeLen * EDGE_HANDLE_STYLE.blockLengthRatio,
            EDGE_HANDLE_STYLE.blockMin,
            EDGE_HANDLE_STYLE.blockMax
        );
        block.scale.set(
            blockLength,
            EDGE_HANDLE_STYLE.blockWidth,
            EDGE_HANDLE_STYLE.blockDepth
        );
    }

    if (line) {
        const lineLen = Math.max(edgeLen, EDGE_HANDLE_STYLE.blockMin);
        const lineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(-lineLen * 0.5, 0, 0),
            new THREE.Vector3(lineLen * 0.5, 0, 0),
        ]);
        if (line.geometry) line.geometry.dispose();
        line.geometry = lineGeometry;
        line.visible = true;
    }
}

function updateRotationHandleVisual(handle, center, corner, planeNormal) {
    if (!handle || !center || !corner) return;
    const normal = getPlaneNormal(planeNormal);
    const outward = corner.clone().sub(center);
    if (outward.lengthSq() > 1e-8) outward.normalize();
    else outward.set(1, 0, 0);

    const handlePos = corner
        .clone()
        .add(outward.clone().multiplyScalar(ROTATION_HANDLE_CORNER_OFFSET))
        .add(normal.clone().multiplyScalar(ROTATION_HANDLE_NORMAL_LIFT));

    handle.position.copy(handlePos);
    handle.userData.baseDir = corner.clone().sub(center);
    if (handle.userData.baseDir.lengthSq() < 1e-8) {
        handle.userData.baseDir.set(1, 0, 0);
    }

    const { u, v, n } = getPlaneBasis(normal);
    const angle = Math.atan2(handle.userData.baseDir.dot(v), handle.userData.baseDir.dot(u));
    const matrix = new THREE.Matrix4().makeBasis(u, v, n);
    handle.quaternion.setFromRotationMatrix(matrix);
    handle.rotateOnAxis(new THREE.Vector3(0, 0, 1), angle);
}

function setHandleHover(handle, isHover) {
    if (!handle) return;
    const target = isHover ? handle.userData.hoverColor : handle.userData.baseColor;
    if (handle.userData.rotateParts?.length) {
        handle.userData.rotateParts.forEach((part) => {
            if (part.material?.color) part.material.color.setHex(target);
            if (part.material && typeof part.material.opacity === "number") {
                const baseOpacity =
                    typeof part.userData?.baseOpacity === "number" ? part.userData.baseOpacity : 0.9;
                part.material.opacity = isHover ? 1 : baseOpacity;
            }
        });
        return;
    }
    if (handle.userData.edgeBlock?.material?.color) {
        handle.userData.edgeBlock.material.color.setHex(target);
        handle.userData.edgeBlock.material.opacity = isHover ? 1 : EDGE_HANDLE_STYLE.blockOpacity;
    } else if (handle.material?.color) {
        handle.material.color.setHex(target);
        if (typeof handle.material.opacity === "number") {
            handle.material.opacity = isHover ? 1 : 0.9;
        }
    }
    if (handle.userData.edgeLine?.material) {
        handle.userData.edgeLine.material.color.setHex(target);
        handle.userData.edgeLine.material.opacity = isHover
            ? EDGE_HANDLE_STYLE.lineHoverOpacity
            : EDGE_HANDLE_STYLE.lineOpacity;
    }
}

function resolveHandleFromEvent(event) {
    const obj = event?.object;
    if (!obj) return null;
    if (obj.isGroup) return obj;
    if (obj.parent?.isGroup) return obj.parent;
    return obj;
}

function disposeObject3D(obj) {
    if (!obj) return;
    obj.userData.disposed = true;
    if (obj.parent) obj.parent.remove(obj);
    obj.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
            if (Array.isArray(child.material)) {
                child.material.forEach((mat) => mat.dispose());
            } else {
                child.material.dispose();
            }
        }
    });
}

// ---------------------------------------------------------------------
// Utility: Create corner + rotation + edge handles
// ---------------------------------------------------------------------
function createCornerHandle(position, index) {
    const ringGeo = new THREE.CircleGeometry(0.11, 32);
    const ringMat = new THREE.MeshBasicMaterial({
        color: HANDLE_COLORS.vertex,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
        side: THREE.DoubleSide,
    });
    const handle = new THREE.Mesh(ringGeo, ringMat);
    handle.position.copy(position);
    handle.userData.vertexIndex = index;
    handle.userData.isCornerHandle = true;
    handle.renderOrder = 2;
    return handle;
}

function createRotationHandle(position, index) {
    const handle = new THREE.Group();
    handle.position.copy(position);
    handle.userData.vertexIndex = index;
    handle.userData.isRotationHandle = true;
    handle.userData.baseColor = HANDLE_COLORS.rotate;
    handle.userData.hoverColor = HANDLE_COLORS.rotateHover;
    handle.renderOrder = 2;

    const arcStart = -ROTATION_HANDLE_STYLE.arc * 0.5;
    const ringGeo = new THREE.RingGeometry(
        ROTATION_HANDLE_STYLE.innerRadius,
        ROTATION_HANDLE_STYLE.outerRadius,
        32,
        1,
        arcStart,
        ROTATION_HANDLE_STYLE.arc
    );
    const ringMat = new THREE.MeshBasicMaterial({
        color: HANDLE_COLORS.rotate,
        transparent: true,
        opacity: 0.35,
        depthTest: false,
        side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.renderOrder = 2;
    ring.frustumCulled = false;
    ring.userData.baseOpacity = 0.35;

    const arrowGeometry = new THREE.ConeGeometry(
        ROTATION_HANDLE_STYLE.arrowSize * 0.65,
        ROTATION_HANDLE_STYLE.arrowSize,
        8
    );
    const arrowMaterial = new THREE.MeshBasicMaterial({
        color: HANDLE_COLORS.rotate,
        transparent: true,
        opacity: 0.9,
        depthTest: false,
    });
    const makeArrowAtArcEnd = (theta, tangentSign) => {
        const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial.clone());
        const tipRadius = ROTATION_HANDLE_STYLE.outerRadius + ROTATION_HANDLE_STYLE.arrowRadius;
        arrow.position.set(Math.cos(theta) * tipRadius, Math.sin(theta) * tipRadius, 0);
        const tangent = new THREE.Vector3(-Math.sin(theta), Math.cos(theta), 0).multiplyScalar(tangentSign);
        arrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, -1, 0), tangent.normalize());
        arrow.renderOrder = 2;
        arrow.frustumCulled = false;
        arrow.userData.baseOpacity = 0.9;
        return arrow;
    };

    const startTheta = arcStart;
    const endTheta = ROTATION_HANDLE_STYLE.arc * 0.5;
    const startArrow = makeArrowAtArcEnd(startTheta, +1);
    const endArrow = makeArrowAtArcEnd(endTheta, -1);

    handle.add(ring, startArrow, endArrow);
    handle.userData.rotateParts = [ring, startArrow, endArrow];

    return handle;
}

function createEdgeHandle(position, index) {
    const group = new THREE.Group();
    group.position.copy(position);
    group.userData.edgeIndex = index;
    group.userData.isEdgeHandle = true;
    group.userData.baseColor = HANDLE_COLORS.translate;
    group.userData.hoverColor = HANDLE_COLORS.translateHover;

    const lineGeometry = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-0.5, 0, 0),
        new THREE.Vector3(0.5, 0, 0),
    ]);
    const lineMaterial = new THREE.LineBasicMaterial({
        color: HANDLE_COLORS.translate,
        transparent: true,
        opacity: EDGE_HANDLE_STYLE.lineOpacity,
        depthTest: false,
    });
    const line = new THREE.Line(lineGeometry, lineMaterial);
    line.raycast = () => {};
    line.renderOrder = 1;

    const blockGeometry = new THREE.BoxGeometry(1, 1, 1);
    const blockMaterial = new THREE.MeshBasicMaterial({
        color: HANDLE_COLORS.translate,
        transparent: true,
        opacity: EDGE_HANDLE_STYLE.blockOpacity,
        depthTest: false,
    });
    const block = new THREE.Mesh(blockGeometry, blockMaterial);
    block.renderOrder = 2;

    group.add(line, block);
    group.userData.edgeLine = line;
    group.userData.edgeBlock = block;
    group.renderOrder = 2;

    return group;
}

// ---------------------------------------------------------------------
// Utility: Signed angle between two vectors around a normal
// ---------------------------------------------------------------------
function signedAngleBetween(v1, v2, normal) {
    const v1n = v1.clone().normalize();
    const v2n = v2.clone().normalize();
    const dot = THREE.MathUtils.clamp(v1n.dot(v2n), -1, 1);
    const angle = Math.acos(dot);
    const cross = new THREE.Vector3().crossVectors(v1n, v2n);
    const sign = Math.sign(cross.dot(normal));
    return angle * sign;
}

// ---------------------------------------------------------------------
// Exit edit mode when click outside handles
// ---------------------------------------------------------------------
function attachPointerDownExitListener() {
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    if (pointerDownListener) return;

    pointerDownListener = (event) => {
        if (!active) return;
        if (
            (obstacleHandles.length === 0) &&
            (rotationHandles.length === 0) &&
            (edgeHandles.length === 0)
        ) return;

        const rect = domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -(((event.clientY - rect.top) / rect.height) * 2 - 1)
        );

        raycaster.setFromCamera(ndc, camera);

        const allHandles = [...obstacleHandles, ...rotationHandles, ...edgeHandles];
        const intersects = raycaster.intersectObjects(allHandles, true);

        if (intersects.length > 0) return;

        disableObstacleVertexDrag();
    };

    domElement.addEventListener('pointerdown', pointerDownListener);
}

function detachPointerDownExitListener() {
    const domElement = sceneManager.renderer.domElement;
    if (pointerDownListener) {
        domElement.removeEventListener('pointerdown', pointerDownListener);
        pointerDownListener = null;
    }
}

// ---------------------------------------------------------------------
// Hover cursor listener
// ---------------------------------------------------------------------
function attachHoverCursorListener(segmentIndex, obstacleIndex) {
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    if (hoverMoveListener) return;

    hoverMoveListener = (event) => {
        if (!active) return;

        const segment = POLYGONS[segmentIndex];
        if (!segment || !Array.isArray(segment.obstructions)) {
            disableObstacleVertexDrag();
            return;
        }

        const obstruction = segment.obstructions[obstacleIndex];
        if (!obstruction) return;

        const rect = domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -(((event.clientY - rect.top) / rect.height) * 2 - 1)
        );

        raycaster.setFromCamera(ndc, camera);

        let intersects = raycaster.intersectObjects(obstacleHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'nwse-resize');
            return;
        }

        intersects = raycaster.intersectObjects(rotationHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'alias');
            return;
        }

        intersects = raycaster.intersectObjects(edgeHandles, true);
        if (intersects.length > 0) {
            setDomCursor(domElement, 'grab');
            return;
        }

        if (obstruction && obstruction.mesh) {
            intersects = raycaster.intersectObject(obstruction.mesh, true);
            if (intersects.length > 0) {
                setDomCursor(domElement, 'grab');
                return;
            }
        }

        setDomCursor(domElement, 'auto');
    };

    domElement.addEventListener('mousemove', hoverMoveListener);
}

function detachHoverCursorListener() {
    const domElement = sceneManager.renderer.domElement;
    if (hoverMoveListener) {
        domElement.removeEventListener('mousemove', hoverMoveListener);
        hoverMoveListener = null;
    }
}

const onEdgeHandleHoverOn = (event) => {
    const handle = resolveHandleFromEvent(event);
    if (!handle?.userData?.isEdgeHandle) return;
    setHandleHover(handle, true);
};

const onEdgeHandleHoverOff = (event) => {
    const handle = resolveHandleFromEvent(event);
    if (!handle?.userData?.isEdgeHandle) return;
    setHandleHover(handle, false);
};

const onRotationHandleHoverOn = (event) => {
    const handle = resolveHandleFromEvent(event);
    if (!handle?.userData?.isRotationHandle) return;
    setHandleHover(handle, true);
};

const onRotationHandleHoverOff = (event) => {
    const handle = resolveHandleFromEvent(event);
    if (!handle?.userData?.isRotationHandle) return;
    setHandleHover(handle, false);
};

export function isObstacleEditDragging() {
    return currentDragMode !== null;
}

// ---------------------------------------------------------------------
// Helper: mark obstacle dirty (unflushed) after edits
// ---------------------------------------------------------------------
function markObstacleDirty(obstruction) {
    if (!obstruction) return;
    obstruction.isFlushed = false;

    // Keep it strictly on parent until next flush
    if (typeof obstruction.parentSegment === "number") {
        obstruction.linkedSegments = [obstruction.parentSegment];
    }
    updateLinkedSegmentsAfterMove(obstruction);
    notifyIrradianceNeedsRecompute("obstacle-moved");
}

export function enableObstacleVertexDrag(segmentIndex, obstacleIndex) {
    disableObstacleVertexDrag();

    const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
    if (!obstruction || !obstruction.cords || obstruction.cords.length < 4) return;

    const parentSegmentIndex =
        typeof obstruction.parentSegment === 'number'
            ? obstruction.parentSegment
            : segmentIndex;

    active = { segmentIndex, obstacleIndex, parentSegmentIndex };

    const plane = createSegmentPlane(parentSegmentIndex);

    setObstacleRaycastEnabled(obstruction, false);

    const basePoints = obstruction.cords.slice(0, obstruction.cords.length - 1);

    const center = new THREE.Vector3();
    basePoints.forEach(p => center.add(p));
    center.multiplyScalar(1 / basePoints.length);

    const scene = sceneManager.scene;
    const domElement = sceneManager.renderer.domElement;
    const camera = sceneManager.camera;

    obstacleHandles.forEach((h) => disposeObject3D(h));
    obstacleHandles = [];

    rotationHandles.forEach((h) => disposeObject3D(h));
    rotationHandles = [];

    edgeHandles.forEach((h) => disposeObject3D(h));
    edgeHandles = [];

    const isCircle = obstruction.type === "circle";

    if (isCircle) {
        let radiusDir = basePoints[0].clone().sub(center);
        if (radiusDir.lengthSq() < 1e-6 && basePoints.length > 1) {
            radiusDir = basePoints[Math.floor(basePoints.length / 4)]
                .clone()
                .sub(center);
        }
        if (radiusDir.lengthSq() < 1e-6) return;

        const radius = Math.sqrt(radiusDir.lengthSq());
        const dirNorm = radiusDir.clone().normalize();

        const scaleHandlePos = center.clone().add(dirNorm.clone().multiplyScalar(radius));
        const translateHandlePos = center.clone().sub(dirNorm.clone().multiplyScalar(radius));

        const scaleHandle = createCornerHandle(scaleHandlePos, 0);
        scaleHandle.userData.isCircleScale = true;
        scene.add(scaleHandle);
        obstacleHandles.push(scaleHandle);

        rotationHandles = [];

        const translateHandle = createEdgeHandle(translateHandlePos, 0);
        translateHandle.userData.centerOffset = translateHandlePos.clone().sub(center);
        translateHandle.userData.isCircleTranslate = true;
        scene.add(translateHandle);
        edgeHandles.push(translateHandle);
    } else {
        for (let i = 0; i < basePoints.length; i++) {
            const cornerPos = basePoints[i].clone();
            const cornerHandle = createCornerHandle(cornerPos, i);
            scene.add(cornerHandle);
            obstacleHandles.push(cornerHandle);

            // Show a single rotation handle (at the first corner), like segments.
            // Dragging it still rotates the whole obstacle around its center.
            if (i === 0) {
                const rotationHandle = createRotationHandle(cornerPos, i);
                rotationHandle.userData.baseDir = cornerPos.clone().sub(center);
                scene.add(rotationHandle);
                rotationHandles.push(rotationHandle);
            }
        }

        for (let i = 0; i < basePoints.length; i++) {
            const p1 = basePoints[i];
            const p2 = basePoints[(i + 1) % basePoints.length];

            const mid = new THREE.Vector3().addVectors(p1, p2).multiplyScalar(0.5);
            const edgeHandle = createEdgeHandle(mid, i);

            edgeHandle.userData.centerOffset = mid.clone().sub(center);

            scene.add(edgeHandle);
            edgeHandles.push(edgeHandle);
        }
    }

    syncHandlesToObstacle(obstruction, plane);
    setDomCursor(domElement, 'nwse-resize');

    // ---------------------------------------------
    // Corner DragControls (resize)
    // ---------------------------------------------
    obstacleDragControls = new DragControls(obstacleHandles, camera, domElement);

    obstacleDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'vertex') return;
        currentDragMode = 'vertex';

        if (rotationDragControls) rotationDragControls.deactivate();
        if (edgeDragControls) edgeDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;
        setDomCursor(domElement, 'grabbing');

        vertexDragState = null;
        if (active) {
            const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
            const obstructionLocal = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            const handle = event.object;

            if (!obstructionLocal || !handle) return;
            setObstacleOffsetVisuals(obstructionLocal, false);

            const originalLinkedSegments = Array.isArray(obstructionLocal.linkedSegments)
                ? obstructionLocal.linkedSegments.slice()
                : [];

            if (obstructionLocal.type === "circle") {
                const baseOriginal = obstructionLocal.cords
                    .slice(0, obstructionLocal.cords.length - 1)
                    .map(p => p.clone());

                const centerOriginal = new THREE.Vector3();
                baseOriginal.forEach(p => centerOriginal.add(p));
                centerOriginal.multiplyScalar(1 / baseOriginal.length);

                vertexDragState = {
                    segmentIndex: segIdx,
                    obstacleIndex: obsIdx,
                    center: centerOriginal,
                    originalPoints: baseOriginal,
                    originalCords: obstructionLocal.cords.map(p => p.clone()),
                    originalIsFlushed: obstructionLocal.isFlushed === true,
                    originalLinkedSegments,
                    moved: false,
                    isCircle: true,
                };
            } else if (typeof handle.userData.vertexIndex === 'number') {
                const vIndex = handle.userData.vertexIndex;
                vertexDragState = {
                    segmentIndex: segIdx,
                    obstacleIndex: obsIdx,
                    vertexIndex: vIndex,
                    lastValidPosition: obstructionLocal.cords[vIndex].clone(),
                    originalCords: obstructionLocal.cords.map(p => p.clone()),
                    originalIsFlushed: obstructionLocal.isFlushed === true,
                    originalLinkedSegments,
                    moved: false,
                    isCircle: false,
                };
            }
        }
    });

    obstacleDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'vertex') return;
        if (!active) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;

        const handle = resolveHandleFromEvent(event);
        if (!handle) return;

        // CIRCLE scale (plane only)
        if (obstruction.type === "circle" && vertexDragState && vertexDragState.isCircle) {
            const { center: centerLocal, originalPoints } = vertexDragState;

            const newPos = new THREE.Vector3();
            plane.projectPoint(handle.position, newPos);

            const dir = newPos.clone().sub(centerLocal);
            if (dir.lengthSq() < 1e-10) return;
            const newRadius = Math.sqrt(dir.lengthSq());
            const radialDir = dir.clone().normalize();

            const finalBasePoints = [];
            for (let i = 0; i < originalPoints.length; i++) {
                const op = originalPoints[i];
                let d = op.clone().sub(centerLocal);
                if (d.lengthSq() < 1e-10) {
                    d = radialDir.clone().multiplyScalar(newRadius);
                } else {
                    d.normalize().multiplyScalar(newRadius);
                }
                const candidate = centerLocal.clone().add(d);

                obstruction.cords[i].copy(candidate);
                finalBasePoints.push(candidate);
            }

            obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

            updateObstacleShape(segIdx, obsIdx, obstruction.cords, { showOffset: false });

            syncHandlesToObstacle(obstruction, plane);

            vertexDragState.moved = true;
            return;
        }

        // RECT/POLYGON vertex move (plane only — NO MODEL RAYCAST)
        const i = handle.userData.vertexIndex;
        const newPos = new THREE.Vector3();
        plane.projectPoint(handle.position, newPos);

        handle.position.copy(newPos);
        obstruction.cords[i].copy(newPos);

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords, { showOffset: false });
        syncHandlesToObstacle(obstruction, plane);

        setObstacleRaycastEnabled(obstruction, false);

        if (vertexDragState) vertexDragState.moved = true;
    });

    obstacleDragControls.addEventListener('dragend', () => {
        if (currentDragMode !== 'vertex') return;

        if (active && vertexDragState) {
            const { segmentIndex: segIdx, obstacleIndex: obsIdx, parentSegmentIndex } = active;
            const obstruction = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            const isCircleLocal = obstruction?.type === "circle";

            if (!isCircleLocal) {
                const { vertexIndex, lastValidPosition } = vertexDragState;

                if (obstruction && obstruction.cords && obstruction.cords.length > 1) {
                    const ownerSegment =
                        typeof obstruction.parentSegment === 'number'
                            ? obstruction.parentSegment
                            : (typeof parentSegmentIndex === 'number'
                                ? parentSegmentIndex
                                : segIdx);

                    const baseVertices = obstruction.cords.slice(0, obstruction.cords.length - 1);
                    let insideCount = 0;
                    for (let v of baseVertices) {
                        if (isWorldPointInsideSegment(v, ownerSegment)) insideCount++;
                    }

                    if (insideCount === 0 && lastValidPosition) {
                        obstruction.cords[vertexIndex].copy(lastValidPosition);
                        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);
                        updateObstacleShape(segIdx, obsIdx, obstruction.cords);
                        syncHandlesToObstacle(obstruction);
                        vertexDragState.moved = false;
                    }
                }
            }

            if (obstruction && obstacleOverlapsPanelsInLinkedSegments(obstruction, segIdx)) {
                updateObstacleShape(segIdx, obsIdx, vertexDragState.originalCords);
                obstruction.isFlushed = vertexDragState.originalIsFlushed;
                obstruction.linkedSegments = Array.isArray(vertexDragState.originalLinkedSegments)
                    ? vertexDragState.originalLinkedSegments.slice()
                    : [];
                updateLinkedSegmentsAfterMove(obstruction);
                syncHandlesToObstacle(obstruction);
                vertexDragState.moved = false;
            }

            if (obstruction) {
                setObstacleOffsetVisuals(obstruction, true);
                refreshPanelOverlapHighlights(obstruction.linkedSegments);
            }
        }

        // Undo/redo + dirty marking
        if (vertexDragState && vertexDragState.moved) {
            const {
                segmentIndex: segIdx,
                obstacleIndex: obsIdx,
                originalCords,
                originalIsFlushed,
                originalLinkedSegments,
            } = vertexDragState;

            const obstructionUndo = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            if (!obstructionUndo) {
                vertexDragState = null;
            } else {
                const finalCords = obstructionUndo.cords.map(p => p.clone());

                // ✅ mark dirty after edit
                markObstacleDirty(obstructionUndo);

                pushUndoAction({
                    label: obstructionUndo.type === "circle" ? "Resize circle obstacle" : "Edit obstacle vertices",
                    undo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, originalCords);
                        o.isFlushed = originalIsFlushed;
                        o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                        updateLinkedSegmentsAfterMove(o);
                    },
                    redo: () => {
                        const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                        if (!o) return;
                        updateObstacleShape(segIdx, obsIdx, finalCords);
                        markObstacleDirty(o);
                    },
                });
            }
        }
        refreshPanelOverlapHighlights();

        vertexDragState = null;
        currentDragMode = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(domElement, 'nwse-resize');

        if (rotationDragControls) rotationDragControls.activate();
        if (edgeDragControls) edgeDragControls.activate();
    });

    // ---------------------------------------------
    // Rotation DragControls
    // ---------------------------------------------
    rotationDragControls = new DragControls(rotationHandles, camera, domElement);
    rotationDragControls.transformGroup = true;
    rotationDragControls.addEventListener('hoveron', onRotationHandleHoverOn);
    rotationDragControls.addEventListener('hoveroff', onRotationHandleHoverOff);

    rotationDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'rotate') return;
        if (!active) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction || obstruction.type === "circle") return;
        setObstacleOffsetVisuals(obstruction, false);

        currentDragMode = 'rotate';

        if (obstacleDragControls) obstacleDragControls.deactivate();
        if (edgeDragControls) edgeDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;

        const { parentSegmentIndex } = active;

        const basePointsOriginal = obstruction.cords
            .slice(0, obstruction.cords.length - 1)
            .map(p => p.clone());

        const centerOriginal = new THREE.Vector3();
        basePointsOriginal.forEach(p => centerOriginal.add(p));
        centerOriginal.multiplyScalar(1 / basePointsOriginal.length);

        const ownerSegment =
            typeof obstruction.parentSegment === 'number'
                ? obstruction.parentSegment
                : (typeof parentSegmentIndex === 'number'
                    ? parentSegmentIndex
                    : segIdx);

        const planeOriginal = createSegmentPlane(ownerSegment);

        const handle = event.object;
        const baseDir = handle.userData.baseDir.clone();

        rotationState = {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane: planeOriginal,
            center: centerOriginal,
            originalPoints: basePointsOriginal,
            baseDir: baseDir,
            originalCords: obstruction.cords.map(p => p.clone()),
            originalIsFlushed: obstruction.isFlushed === true,
            originalLinkedSegments: Array.isArray(obstruction.linkedSegments) ? obstruction.linkedSegments.slice() : [],
            moved: false,
            currentAngle: 0,
        };

        setDomCursor(sceneManager.renderer.domElement, 'alias');
    });

    rotationDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'rotate') return;
        if (!rotationState) return;

        const {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane,
            center,
            originalPoints,
            baseDir,
        } = rotationState;

        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction || obstruction.type === "circle") return;

        const handle = event.object;

        const projected = new THREE.Vector3();
        plane.projectPoint(handle.position, projected);
        handle.position.copy(projected);

        const currentDir = projected.clone().sub(center);
        if (currentDir.lengthSq() < 1e-8) return;

        const normal = plane.normal.clone().normalize();
        const angle = signedAngleBetween(baseDir, currentDir, normal);

        rotationState.currentAngle = angle;

        for (let i = 0; i < originalPoints.length; i++) {
            const v = originalPoints[i].clone().sub(center);
            v.applyAxisAngle(normal, angle);
            const rotatedPos = center.clone().add(v);
            obstruction.cords[i].copy(rotatedPos);
        }

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords, { showOffset: false });
        syncHandlesToObstacle(obstruction, plane);

        if (Math.abs(angle) > 1e-4 && rotationState) rotationState.moved = true;

        setDomCursor(domElement, 'alias');
    });

    rotationDragControls.addEventListener('dragend', () => {
        if (currentDragMode !== 'rotate') return;

        if (rotationState && rotationState.moved) {
            const {
                segmentIndex: segIdx,
                obstacleIndex: obsIdx,
                plane,
                center,
                originalPoints,
                originalCords,
                originalIsFlushed,
                originalLinkedSegments,
                currentAngle,
            } = rotationState;

            const obstruction = POLYGONS[segIdx]?.obstructions?.[obsIdx];
            if (obstruction && obstruction.type !== "circle") {
                const normal = plane.normal.clone().normalize();

                // ✅ Final rotation (NO SNAP) — snap only when user clicks ✅ tick
                const finalBase = originalPoints.map(p => {
                    const v = p.clone().sub(center);
                    v.applyAxisAngle(normal, currentAngle);
                    return center.clone().add(v);
                });

                const finalCords = finalBase.map(p => p.clone());
                finalCords.push(finalCords[0].clone());

                updateObstacleShape(segIdx, obsIdx, finalCords);

                const overlapsPanels = obstacleOverlapsPanelsInLinkedSegments(obstruction, segIdx);
                if (overlapsPanels) {
                    updateObstacleShape(segIdx, obsIdx, originalCords);
                    obstruction.isFlushed = originalIsFlushed;
                    obstruction.linkedSegments = Array.isArray(originalLinkedSegments)
                        ? originalLinkedSegments.slice()
                        : [];
                    updateLinkedSegmentsAfterMove(obstruction);
                    rotationState.moved = false;
                } else {
                    // mark dirty
                    markObstacleDirty(obstruction);

                    const finalCordsSnapshot = obstruction.cords.map(p => p.clone());

                    pushUndoAction({
                        label: "Rotate obstacle",
                        undo: () => {
                            const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                            if (!o) return;
                            updateObstacleShape(segIdx, obsIdx, originalCords);
                            o.isFlushed = originalIsFlushed;
                            o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                            updateLinkedSegmentsAfterMove(o);
                        },
                        redo: () => {
                            const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                            if (!o) return;
                            updateObstacleShape(segIdx, obsIdx, finalCordsSnapshot);
                            markObstacleDirty(o);
                        },
                    });
                }
                setObstacleOffsetVisuals(obstruction, true);
                refreshPanelOverlapHighlights(obstruction.linkedSegments);
            }
        }
        refreshPanelOverlapHighlights();

        currentDragMode = null;
        rotationState = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(sceneManager.renderer.domElement, 'alias');

        if (obstacleDragControls) obstacleDragControls.activate();
        if (edgeDragControls) edgeDragControls.activate();
    });

    // ---------------------------------------------
    // Edge DragControls (translate)
    // ---------------------------------------------
    edgeDragControls = new DragControls(edgeHandles, camera, domElement);
    edgeDragControls.transformGroup = true;
    edgeDragControls.addEventListener('hoveron', onEdgeHandleHoverOn);
    edgeDragControls.addEventListener('hoveroff', onEdgeHandleHoverOff);

    edgeDragControls.addEventListener('dragstart', (event) => {
        if (currentDragMode && currentDragMode !== 'translate') return;
        if (!active) return;

        currentDragMode = 'translate';

        if (obstacleDragControls) obstacleDragControls.deactivate();
        if (rotationDragControls) rotationDragControls.deactivate();

        if (sceneManager.controls) sceneManager.controls.enabled = false;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx, parentSegmentIndex } = active;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;
        setObstacleOffsetVisuals(obstruction, false);

        const basePointsOriginal = obstruction.cords
            .slice(0, obstruction.cords.length - 1)
            .map(p => p.clone());

        const centerOriginal = new THREE.Vector3();
        basePointsOriginal.forEach(p => centerOriginal.add(p));
        centerOriginal.multiplyScalar(1 / basePointsOriginal.length);

        const ownerSegment =
            typeof obstruction.parentSegment === 'number'
                ? obstruction.parentSegment
                : (typeof parentSegmentIndex === 'number'
                    ? parentSegmentIndex
                    : segIdx);

        const planeOriginal = createSegmentPlane(ownerSegment);

        const originalCords = obstruction.cords.map(p => p.clone());
        const originalLinkedSegments = Array.isArray(obstruction.linkedSegments) ? obstruction.linkedSegments.slice() : [];

        translateState = {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane: planeOriginal,
            center: centerOriginal,
            originalPoints: basePointsOriginal,
            originalCords,
            originalIsFlushed: obstruction.isFlushed === true,
            originalLinkedSegments,
            ownerSegmentIndex: ownerSegment,
            moved: false,
        };

        setDomCursor(domElement, 'grabbing');
    });

    edgeDragControls.addEventListener('drag', (event) => {
        if (currentDragMode !== 'translate') return;
        if (!translateState) return;

        const { segmentIndex: segIdx, obstacleIndex: obsIdx, plane, center, originalPoints } = translateState;
        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];
        if (!obstruction) return;

        const handle = event.object;

        const projected = new THREE.Vector3();
        plane.projectPoint(handle.position, projected);
        handle.position.copy(projected);

        const centerOffset = handle.userData.centerOffset.clone();
        const newCenter = projected.clone().sub(centerOffset);
        const delta = newCenter.clone().sub(center);

        for (let i = 0; i < originalPoints.length; i++) {
            const moved = originalPoints[i].clone().add(delta);
            obstruction.cords[i].copy(moved);
        }

        obstruction.cords[obstruction.cords.length - 1].copy(obstruction.cords[0]);

        updateObstacleShape(segIdx, obsIdx, obstruction.cords, { showOffset: false });
        syncHandlesToObstacle(obstruction, plane);

        if (delta.lengthSq() > 1e-8 && translateState) translateState.moved = true;

        setDomCursor(domElement, 'grabbing');
    });

    edgeDragControls.addEventListener('dragend', (event) => {
        if (currentDragMode !== 'translate') return;
        if (!translateState) return;

        const {
            segmentIndex: segIdx,
            obstacleIndex: obsIdx,
            plane,
            center,
            originalPoints,
            originalCords,
            originalIsFlushed,
            originalLinkedSegments,
            ownerSegmentIndex,
        } = translateState;

        const obstruction = POLYGONS[segIdx].obstructions[obsIdx];

        if (obstruction) {
            const handle = resolveHandleFromEvent(event) || event.object;

            const projected = new THREE.Vector3();
            plane.projectPoint(handle.position, projected);

            const centerOffset = handle.userData.centerOffset.clone();
            const newCenter = projected.clone().sub(centerOffset);
            const diff = newCenter.clone().sub(center);

            // ✅ Final translate (NO SNAP) — snap only when user clicks ✅ tick
            const finalBase = originalPoints.map(p => p.clone().add(diff));

            const finalCords = finalBase.map(p => p.clone());
            finalCords.push(finalCords[0].clone());

            const baseVertices = finalCords.slice(0, finalCords.length - 1);
            let insideCount = 0;
            baseVertices.forEach(v => {
                if (isWorldPointInsideSegment(v, ownerSegmentIndex)) insideCount++;
            });

            if (insideCount === 0) {
                updateObstacleShape(segIdx, obsIdx, originalCords);
                updateLinkedSegmentsAfterMove(obstruction);
                syncHandlesToObstacle(obstruction);
                translateState.moved = false;
                refreshPanelOverlapHighlights(obstruction.linkedSegments);
            } else {
                updateObstacleShape(segIdx, obsIdx, finalCords);
                const overlapsPanels = obstacleOverlapsPanelsInLinkedSegments(obstruction, segIdx);
                if (overlapsPanels) {
                    updateObstacleShape(segIdx, obsIdx, originalCords);
                    obstruction.isFlushed = originalIsFlushed;
                    obstruction.linkedSegments = Array.isArray(originalLinkedSegments)
                        ? originalLinkedSegments.slice()
                        : [];
                    updateLinkedSegmentsAfterMove(obstruction);
                    translateState.moved = false;
                } else {
                    // mark dirty
                    markObstacleDirty(obstruction);

                    const finalCordsSnapshot = obstruction.cords.map(p => p.clone());

                    if (translateState.moved) {
                        pushUndoAction({
                            label: "Move obstacle",
                            undo: () => {
                                const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                                if (!o) return;
                                updateObstacleShape(segIdx, obsIdx, originalCords);
                                o.isFlushed = originalIsFlushed;
                                o.linkedSegments = Array.isArray(originalLinkedSegments) ? originalLinkedSegments.slice() : [];
                                updateLinkedSegmentsAfterMove(o);
                            },
                            redo: () => {
                                const o = POLYGONS[segIdx]?.obstructions?.[obsIdx];
                                if (!o) return;
                                updateObstacleShape(segIdx, obsIdx, finalCordsSnapshot);
                                markObstacleDirty(o);
                            },
                        });
                    }
                }
                refreshPanelOverlapHighlights(obstruction.linkedSegments);
            }

            // sync handles to current geometry
            syncHandlesToObstacle(obstruction, plane);
            setObstacleOffsetVisuals(obstruction, true);
        }
        refreshPanelOverlapHighlights();

        currentDragMode = null;
        translateState = null;

        if (sceneManager.controls) sceneManager.controls.enabled = true;
        setDomCursor(sceneManager.renderer.domElement, 'auto');

        if (obstacleDragControls) obstacleDragControls.activate();
        if (rotationDragControls) rotationDragControls.activate();
    });

    attachHoverCursorListener(segmentIndex, obstacleIndex);
    attachPointerDownExitListener();
}

export function disableObstacleVertexDrag() {
    const domElement = sceneManager.renderer.domElement;

    currentDragMode = null;

    detachPointerDownExitListener();
    detachHoverCursorListener();

    if (obstacleDragControls) {
        obstacleDragControls.deactivate();
        obstacleDragControls.dispose();
        obstacleDragControls = null;
    }

    if (rotationDragControls) {
        rotationDragControls.deactivate();
        rotationDragControls.dispose();
        rotationDragControls = null;
    }

    if (edgeDragControls) {
        edgeDragControls.deactivate();
        edgeDragControls.dispose();
        edgeDragControls = null;
    }

    obstacleHandles.forEach((h) => disposeObject3D(h));
    obstacleHandles = [];

    rotationHandles.forEach((h) => disposeObject3D(h));
    rotationHandles = [];

    edgeHandles.forEach((h) => disposeObject3D(h));
    edgeHandles = [];

    rotationState = null;
    translateState = null;
    vertexDragState = null;

    if (active) {
        const { segmentIndex, obstacleIndex } = active;
        const obstruction = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
        if (obstruction) {
            setObstacleRaycastEnabled(obstruction, true);
        }
    }

    active = null;

    setDomCursor(domElement, 'auto');
}
